import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useMe } from './MeContextProvider'
import { useBreadcrumbState } from './Breadcrumb'
import { SearchPalette } from './search/SearchPalette'

export interface TopbarProps {
  onToggleSidebar: () => void
}

export function Topbar({ onToggleSidebar }: TopbarProps) {
  const { firmName, navItems } = useMe()
  const location = useLocation()
  const { crumbs } = useBreadcrumbState()

  // Okruszki z nawigacji: „<nazwa modułu> / <pozycja menu>".
  const derived = useMemo(() => {
    if (location.pathname === '/') return ['Pulpit']
    const matches = navItems
      .filter((item) => item.route !== '/' && location.pathname.startsWith(item.route))
      .sort((a, b) => b.route.length - a.route.length)
    const best = matches[0]
    return best ? [best.moduleName, best.label] : []
  }, [location.pathname, navItems])

  const trail = crumbs ?? derived

  return (
    <header className="flex h-topbar shrink-0 items-center gap-4 border-b border-line-soft bg-card px-[26px]">
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label="Menu"
        className="-ml-2 shrink-0 rounded p-2 text-muted transition-colors duration-fast hover:bg-[#eef1f4] hover:text-ink shell:hidden"
      >
        <svg viewBox="0 0 16 16" aria-hidden className="h-4 w-4">
          <path d="M1.5 4h13M1.5 8h13M1.5 12h13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>

      <nav aria-label="Ścieżka" className="min-w-0 truncate text-small text-muted">
        {trail.join(' / ')}
      </nav>

      <div className="ml-auto flex items-center gap-5">
        <SearchBox />
        <div className="hidden items-center gap-2 lg:flex">
          <span className="whitespace-nowrap text-base text-ink">{firmName}</span>
        </div>
      </div>
    </header>
  )
}

/**
 * Globalne wyszukiwanie — pole otwiera paletę (⌘K / Ctrl+K).
 *
 * Renderuje się WYŁĄCZNIE, gdy moduł `search` przyszedł w `/api/me/context`: bez wpisu
 * w `MODULES_ENABLED` (albo bez widoczności dla roli) nie ma ani endpointu, ani pola —
 * tak samo jak z pozycjami menu.
 */
function SearchBox() {
  const { hasModule, can } = useMe()
  const [open, setOpen] = useState(false)
  const enabled = hasModule('search') && can('search.use')

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!enabled) return
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])

  if (!enabled) return null

  return (
    <>
      <div className="relative hidden md:block">
        <svg
          viewBox="0 0 14 14"
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-muted"
        >
          <circle cx="6" cy="6" r="4.3" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M9.4 9.4 13 13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        {/* Przycisk, nie <input>: pisanie odbywa się w palecie, a dwa pola tekstowe
            trzymające tę samą frazę rozjeżdżają się przy każdym otwarciu. */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Szukaj (Ctrl+K)"
          className="h-[32px] w-[240px] rounded border border-line-soft bg-bg pl-8 pr-12 text-left text-small text-muted transition-colors duration-fast hover:border-line xl:w-[320px]"
        >
          Szukaj spraw, dokumentów, klienta…
        </button>
        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-[2px] border border-line-soft bg-card px-1.5 py-0.5 font-mono text-[11px] text-muted">
          ⌘K
        </kbd>
      </div>
      <SearchPalette open={open} onClose={close} />
    </>
  )
}

